/**
 * Where this one script's runs notify — a user's own default set, or a dedicated
 * override set just for this script. Only shown once someone has connected Microsoft
 * Teams at all; otherwise a quiet link to go do that first, rather than a picker with
 * nothing to pick from.
 *
 * An override replaces the default set entirely rather than adding to it — the moment
 * you add a destination here, this script stops inheriting the default and gets its own
 * independent list. That's why an inherited (non-override) row has no remove button:
 * removing one would be ambiguous without first deciding whether to start an override.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import type { TeamsTargetOption } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { TeamsTargetPicker } from "@/components/TeamsTargetPicker";
import { Badge, Notice } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

export function ScriptNotifyTarget({ scriptId }: { scriptId: string }) {
  const { data: connection, loading: connectionLoading } = useAsync(
    () => endpoints.teamsConnection(),
    [],
  );
  const { data: targets, reload } = useAsync(
    () => endpoints.teamsTargetSet(scriptId),
    [scriptId, connection?.connected],
    { enabled: !!connection?.connected },
  );

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  if (connectionLoading || !connection?.connected) {
    return connectionLoading ? null : (
      <Link to="/account/apps/teams" className="muted" style={{ fontSize: 12.5 }}>
        Connect Microsoft Teams to get notified about this script's runs
      </Link>
    );
  }

  const list = targets ?? [];
  const isOverride = list.some((t) => t.is_override);
  const selectedRefs = new Set(list.map((t) => t.graph_ref));

  const add = async (option: TeamsTargetOption) => {
    // Always scoped to this script — never the shared default, regardless of whether
    // what's currently showing is inherited or already an override.
    await endpoints.addTeamsTarget({ ...option, script_id: scriptId });
    reload();
  };

  const remove = async (targetId: string) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.removeTeamsTarget(targetId);
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const useDefault = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.clearTeamsTargetOverrides(scriptId);
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 6 }}>
      {problem && <Notice tone="danger">{problem}</Notice>}
      <div className="row" style={{ gap: 8, alignItems: "center", fontSize: 12.5, flexWrap: "wrap" }}>
        <span className="muted">Notifies:</span>
        {list.length === 0 ? (
          <span className="muted">not set</span>
        ) : (
          list.map((target) => (
            <span key={target.id} className="row" style={{ gap: 4, alignItems: "center" }}>
              <Badge tone="accent">{target.label}</Badge>
              {target.is_override && (
                <button
                  type="button"
                  className="button button--small button--ghost"
                  disabled={busy}
                  onClick={() => remove(target.id)}
                  aria-label={`Remove ${target.label}`}
                >
                  ×
                </button>
              )}
            </span>
          ))
        )}
        <span className="faint">{isOverride ? "(overridden for this script)" : "(your default)"}</span>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <TeamsTargetPicker
          selectedRefs={isOverride ? selectedRefs : new Set()}
          onAdd={add}
          busy={busy}
          buttonLabel={isOverride ? "Add another override" : "Override for this script"}
        />
        {isOverride && (
          <button
            type="button"
            className="button button--small button--ghost"
            disabled={busy}
            onClick={useDefault}
          >
            Use default instead
          </button>
        )}
      </div>
    </div>
  );
}
